/**
 * AddTokenModal — modal hosting the `<TokenInput>` autocomplete used to
 * add a new token to the active portfolio.
 *
 * Extracted from `PortfolioDashboard.tsx` in Sprint V2.B3 (cf.
 * `docs/v2/PORTFOLIO_OFFCHAIN_REFONTE.md`). Was inline JSX with a
 * hand-rolled overlay; now reusable + testable.
 *
 * The overlay uses `className="modal-overlay"` (defined in
 * `src/styles/index.css`) for the backdrop + a few inline styles to
 * override the CSS default `display: none` and to size the inner
 * card. This matches the pattern used by `<PortfolioModal>`,
 * `<QRCodeImportModal>`, etc.
 */

import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { TokenInput } from '../TokenInput';
import type { Coin } from '../../types';

interface AddTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Forwarded to `<TokenInput>`'s `onAdd`. */
  onAdd: (coinId: string, symbol: string) => void;
  /** Forwarded to `<TokenInput>`'s `coins`. */
  coins: Coin[];
}

export function AddTokenModal({ isOpen, onClose, onAdd, coins }: AddTokenModalProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-token-modal-title"
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: '90%', maxWidth: '500px', padding: '24px' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <h3 id="add-token-modal-title" style={{ margin: 0 }}>
            {t('portfolioDashboard.addFirstToken')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Fermer')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Icons.X size={20} />
          </button>
        </div>

        <p style={{ color: 'var(--muted)', marginBottom: '16px' }}>
          {t('portfolioDashboard.searchSelectToken')}
        </p>

        <TokenInput onAdd={onAdd} coins={coins} />
      </div>
    </div>
  );
}
